import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Form,
  Button,
  Card,
  Row,
  Col,
  Alert,
  Spinner,
  Container
} from 'react-bootstrap';
import { FaSave, FaArrowLeft } from 'react-icons/fa';

const EditAppointment = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [patients, setPatients] = useState([]);
  
  // Form data state
  const [appointmentData, setAppointmentData] = useState({
    patientId: '',
    patientName: '',
    patientInternalId: '',
    doctorId: '',
    appointmentDate: '',
    appointmentTime: '',
    reason: '',
    status: 'scheduled',
    notes: '',
    appointmentType: 'regular',
    roomNumber: ''
  });
  
  // Fetch appointment data on mount
  useEffect(() => {
    const fetchAppointmentData = async () => {
      try {
        // First fetch appointment data
        const appointmentRes = await axios.get(`/api/appointments/${id}`);
        const appointment = appointmentRes.data;
        
        console.log('DEBUG: Appointment data:', appointment);
        console.log('DEBUG: Appointment patientId:', appointment.patientId, 'Type:', typeof appointment.patientId);
        console.log('DEBUG: Appointment Patient object:', appointment.Patient);
        
        // Then fetch doctors and patients
        const [doctorsRes, patientsRes] = await Promise.all([
          axios.get('/api/doctors.php'),
          axios.get('/api/patients.php')
        ]);
        
        console.log('DEBUG: Patients loaded:', patientsRes.data.length);
        console.log('DEBUG: First 3 patients:', patientsRes.data.slice(0, 3));
        
        setDoctors(doctorsRes.data);
        setPatients(patientsRes.data);
        
        // IMPORTANT: Use Patient object from appointment response
        // This contains the correct patient data even if patient is archived
        const patientData = appointment.Patient;
        
        console.log('DEBUG: Using patient data from appointment:', patientData);
        console.log('DEBUG: Doctor ID from appointment:', appointment.doctorId);
        console.log('DEBUG: Doctor object from appointment:', appointment.Doctor);
        
        // Extract date and time from the appointment
        let formattedDate = '';
        let formattedTime = '';
        
        if (appointment.appointmentDate) {
          formattedDate = appointment.appointmentDate;
        } else if (appointment.appointmentDateTime) {
          const dateTime = new Date(appointment.appointmentDateTime);
          const year = dateTime.getFullYear();
          const month = String(dateTime.getMonth() + 1).padStart(2, '0');
          const day = String(dateTime.getDate()).padStart(2, '0');
          formattedDate = `${year}-${month}-${day}`;
        }
        
        if (appointment.appointmentTime) {
          formattedTime = appointment.appointmentTime;
        } else if (appointment.appointmentDateTime) {
          const dateTime = new Date(appointment.appointmentDateTime);
          const hours = String(dateTime.getHours()).padStart(2, '0');
          const minutes = String(dateTime.getMinutes()).padStart(2, '0');
          formattedTime = `${hours}:${minutes}`;
        }
        
        console.log('DEBUG: Final patient data to use:', patientData);
        console.log('DEBUG: Setting doctorId to:', parseInt(appointment.doctorId));
        console.log('DEBUG: Available doctors:', doctorsRes.data.map(d => ({id: d.id, name: d.name})));
        
        // Store patient data in state for rendering
        setAppointmentData({
          patientId: patientData?.patientId || '',
          patientName: patientData?.name || 'Unknown Patient',
          patientInternalId: parseInt(appointment.patientId),
          doctorId: parseInt(appointment.doctorId),
          appointmentDate: formattedDate,
          appointmentTime: formattedTime,
          reason: appointment.reason || '',
          status: appointment.status,
          notes: appointment.notes || '',
          appointmentType: appointment.appointmentType || 'regular',
          roomNumber: appointment.roomNumber || ''
        });
        
        setError(null);
      } catch (err) {
        console.error('Error loading appointment data:', err);
        setError('Failed to load appointment data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAppointmentData();
  }, [id]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAppointmentData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Send internal patient ID to backend
      const submitData = {
        ...appointmentData,
        patientId: appointmentData.patientInternalId, // Use internal ID
        id: id
      };
      delete submitData.patientInternalId; // Remove extra field
      
      console.log('Submitting appointment update:', submitData);
      
      await axios.put(`/api/appointments.php?id=${id}`, submitData);
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/appointments');
      }, 1500);
    } catch (err) {
      console.error('Error updating appointment:', err);
      console.error('Error response data:', err.response?.data);
      console.error('Error response status:', err.response?.status);
      
      const errorMessage = err.response?.data?.error || 'Failed to update appointment. Please try again.';
      setError(`Error! ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <Container className="my-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading appointment data...</p>
      </Container>
    );
  }
  
  return (
    <div className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Edit Appointment</h2>
        <Button variant="outline-secondary" onClick={() => navigate(-1)}>
          <FaArrowLeft className="me-2" /> Back
        </Button>
      </div>
      
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">Appointment updated successfully!</Alert>}
      
      <Card>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Patient</Form.Label>
                  <Form.Control
                    type="text"
                    value={appointmentData.patientName && appointmentData.patientId 
                      ? `${appointmentData.patientName} (${appointmentData.patientId})` 
                      : 'Loading patient...'}
                    disabled
                    readOnly
                    style={{backgroundColor: '#e9ecef', cursor: 'not-allowed'}}
                  />
                  <Form.Text className="text-muted">
                    Patient cannot be changed after appointment is created
                  </Form.Text>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Doctor</Form.Label>
                  <Form.Select
                    name="doctorId"
                    value={appointmentData.doctorId || ''}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Doctor</option>
                    {doctors.map(doctor => (
                      <option key={doctor.id} value={doctor.id}>
                        {doctor.name} ({doctor.specialization})
                      </option>
                    ))}
                  </Form.Select>
                  {appointmentData.doctorId && (
                    <Form.Text className="text-muted">
                      Current: Doctor ID {appointmentData.doctorId}
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Appointment Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="appointmentDate"
                    value={appointmentData.appointmentDate}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Appointment Time</Form.Label>
                  <Form.Control
                    type="time"
                    name="appointmentTime"
                    value={appointmentData.appointmentTime}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    name="status"
                    value={appointmentData.status}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="scheduled">Scheduled</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="no-show">No Show</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Reason for Visit</Form.Label>
                  <Form.Control
                    type="text"
                    name="reason"
                    value={appointmentData.reason}
                    onChange={handleInputChange}
                    placeholder="Brief reason for appointment"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Appointment Type</Form.Label>
                  <Form.Select
                    name="appointmentType"
                    value={appointmentData.appointmentType}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="regular">Regular</option>
                    <option value="followup">Follow-up</option>
                    <option value="emergency">Emergency</option>
                    <option value="walkin">Walk-in</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Room Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="roomNumber"
                    value={appointmentData.roomNumber}
                    onChange={handleInputChange}
                    placeholder="Room number (optional)"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="notes"
                value={appointmentData.notes}
                onChange={handleInputChange}
                placeholder="Additional notes or instructions"
              />
            </Form.Group>
            
            <div className="d-flex justify-content-end">
              <Button 
                variant="primary" 
                type="submit" 
                disabled={loading}
                className="d-flex align-items-center"
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Updating...
                  </>
                ) : (
                  <>
                    <FaSave className="me-2" /> Save Changes
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default EditAppointment; 

